import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ChargingStationLocationService } from 'src/app/shared/services/charging-station-location.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { BehaviorSubject, map, Observable, of, startWith, take } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import {
  AppVisibilityStatus,
  LocationAppVisibility,
  ChargingStationLocationDetails,
  ChargingStationLocationSearchEvent,
  CreateChargingStationLocation,
  LocationCategory,
  LocationFacility,
  LocationProduct,
} from '../charging-station-location-model';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { Router } from '@angular/router';
import { DefaultAccessGroup } from '../../access-groups/access-group.model';
import { LoadingSpinnerComponent } from 'src/app/core/loading-spinner/loading-spinner.component';
import { InputMaskModule } from '@ngneat/input-mask';
import { catchError, filter, shareReplay, switchMap, tap } from 'rxjs/operators';
import { MatIcon } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { ChargingStationLocationEditDialogStep1Component, Step1Form } from './steps/step1/step1.component';
import {
  ChargingStationLocationCreatedEditDialogService,
  ChargingStationLocationDialogData,
} from './charging-station-location-created-edit-dialog.service';
import { AddressEligibilityInput, dynamicPricePolicyEligibility, netAreaAddressKey } from './dynamic-price-policy-eligibility';
import { AddressForm, ChargingStationLocationEditDialogStep2Component, Step2Form } from './steps/step2/step2.component';
import {
  ADDRESS_MAX_LENGTH,
  doorValidator,
  floorValidator,
  houseLetterValidator,
  houseNumberValidator,
  manualAddressOnly,
  postalCodeValidator,
} from './steps/step2/danish-address-rules';
import { ChargingStationLocationEditDialogStep3Component, Step3Form } from './steps/step3/step3.component';
import { Step4Form, ChargingStationLocationEditDialogStep4Component } from './steps/step4/step4.component';
import { AdmFormAccordionComponent, FormAccordionPanelDirective } from 'src/app/shared/adm-form-accordion/adm-form-accordion.component';
import { PricePolicy, PricePolicyType } from 'src/app/features/prices/price-policies/interfaces/price-policy';
import { InitialAddress } from 'src/app/shared/components/dep-address-typeahead/dep-address-typeahead.component';
import { Address } from 'src/app/shared/models/address';
import { Guid } from 'guid-typescript';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { FEATURE_FLAGS } from 'src/app/shared/services/feature-flags.const';

export interface ChargingStationLocationCreateModalForm {
  step1: FormGroup<Step1Form>;
  step2: FormGroup<Step2Form>;
  step3: FormGroup<Step3Form>;
  step4: FormGroup<Step4Form>;
  id: FormControl<number | null>;
  chargingStationLocationIdentifier: FormControl<string | null>;
}

const LOCATION_ERROR_MESSAGES: Record<number, string> = {
  100212: 'Oprettelse af lokationen fejlede i CSO.',
  100611: 'Lokationen kunne ikke findes og ændringerne er dermed ikke blevet gemt.',
  100612: 'Prispolitik skal vælges, når OK-måler ændres, eller når adressen ikke længere har et gyldigt netområde.',
  100613: 'Den valgte prispolitik passer ikke til den valgte OK-måler-indstilling. Vælg en anden prispolitik og prøv igen.',
  100614: 'El-aftagenummer er påkrævet, når OK-måler er aktiveret.',
  100615: 'El-aftagenummer må kun indeholde tal.',
  100616: 'Ekskludéring af elpris i beregning kan ikke aktiveres, når OK-måler er slået til.',
  100620: 'Lokationen har ikke et gyldigt netområde, så der kan ikke vælges en dynamisk prispolitik.',
  100621: 'Opdatering af lokationen fejlede i CSO.',
  100622: `Vejnavn må højst være ${ADDRESS_MAX_LENGTH.street} tegn.`,
  100623: 'Husnummer skal være et tal mellem 1 og 999.',
  100624: 'Bogstav skal være ét stort bogstav fra A-Z.',
  100625: 'Etage skal være st, kl, k2-k9 eller 1-99.',
  100626: 'Dør skal være tv, th, mf, et tal eller op til 4 tegn.',
  100627: 'Postnummer skal være 4 cifre.',
  100628: `By må højst være ${ADDRESS_MAX_LENGTH.town} tegn.`,
};

@Component({
  selector: 'adm-charging-station-location-create-edit-modal',
  templateUrl: './charging-station-location-create-edit-modal.html',
  styleUrls: ['./charging-station-location-create-edit-modal.scss'],
  imports: [
    LoadingSpinnerComponent,
    InputMaskModule,
    MatIcon,
    MatDialogContent,
    MatDialogActions,
    ReactiveFormsModule,
    CommonModule,
    MatAutocompleteModule,
    MatButton,
    MatDialogModule,
    ChargingStationLocationEditDialogStep1Component,
    ChargingStationLocationEditDialogStep2Component,
    ChargingStationLocationEditDialogStep3Component,
    ChargingStationLocationEditDialogStep4Component,
    AdmFormAccordionComponent,
    FormAccordionPanelDirective,
  ],
  providers: [ChargingStationLocationCreatedEditDialogService],
})
export class ChargingStationLocationCreateEditModalComponent {
  service = inject(ChargingStationLocationCreatedEditDialogService);
  dialogRef = inject<MatDialogRef<ChargingStationLocationCreateEditModalComponent>>(MatDialogRef);
  private chargingStationLocationService = inject(ChargingStationLocationService);
  private snackbarService = inject(SnackbarService);
  private router = inject(Router);
  pageEvent = new PageEvent();
  protected pricePolicyChangeRequired = signal(false);
  protected pricePolicyResetTrigger = signal({ reset: false });
  private selectedPricePolicyIsDynamic = false;
  private initialPricePolicyId = signal<number | null>(null);
  private lastHasOkPowerMeter = false;
  protected initialHasOkPowerMeter = false;
  protected initialAddress = signal<InitialAddress | null>(null);
  private initialNetAreaAddress = signal<AddressEligibilityInput | null>(null);
  private initialCanHaveDynamicPricePolicy = signal<boolean | null>(null);

  readonly dialogData = inject<ChargingStationLocationDialogData | null>(MAT_DIALOG_DATA, { optional: true });

  locationForm: FormGroup<ChargingStationLocationCreateModalForm> = new FormGroup<ChargingStationLocationCreateModalForm>({
    step1: new FormGroup<Step1Form>({
      organizationId: new FormControl<number | null>(null, { validators: [Validators.required] }),
      departmentId: new FormControl<string | null>(null, { validators: [Validators.required] }),
      category: new FormControl<LocationCategory>(LocationCategory.Public, { validators: [Validators.required], nonNullable: true }),
    }),
    step2: new FormGroup<Step2Form>({
      name: new FormControl<string | null>(null, { validators: [Validators.required, Validators.maxLength(75)] }),
      addressWithoutDARValidation: new FormControl<boolean>(false, { nonNullable: true }),
      address: new FormGroup<AddressForm>({
        street: new FormControl<string>('', {
          validators: [Validators.required, Validators.maxLength(ADDRESS_MAX_LENGTH.street)],
          nonNullable: true,
        }),
        houseLetter: new FormControl<string>('', {
          validators: [Validators.maxLength(ADDRESS_MAX_LENGTH.letter), manualAddressOnly(houseLetterValidator)],
          nonNullable: true,
        }),
        houseNumber: new FormControl<string>('', {
          validators: [Validators.required, manualAddressOnly(houseNumberValidator)],
          nonNullable: true,
        }),
        town: new FormControl<string>('', {
          validators: [Validators.required, Validators.maxLength(ADDRESS_MAX_LENGTH.town)],
          nonNullable: true,
        }),
        zipCode: new FormControl<string>('', {
          validators: [Validators.required, Validators.maxLength(4), manualAddressOnly(postalCodeValidator)],
          nonNullable: true,
        }),
        floor: new FormControl<string | null>(null, [Validators.maxLength(ADDRESS_MAX_LENGTH.floor), manualAddressOnly(floorValidator)]),
        door: new FormControl<string | null>(null, [Validators.maxLength(ADDRESS_MAX_LENGTH.door), manualAddressOnly(doorValidator)]),
        darId: new FormControl<string | null>(null),
      }),
      locationAddress: new FormControl<string | null>(''),
      latitude: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
      longitude: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
      meteringPointIdentifiers: new FormArray<FormControl<string>>([]),
      hasOkPowerMeter: new FormControl<boolean>(false, { nonNullable: true }),
      restricted: new FormControl<boolean>(false, { nonNullable: true }),
      locationAppVisibilityStatus: new FormControl<AppVisibilityStatus>(AppVisibilityStatus.Hidden, {
        validators: [Validators.required],
        nonNullable: true,
      }),
      locationAppVisibility: new FormControl<LocationAppVisibility>(LocationAppVisibility.Both, {
        validators: [Validators.required],
        nonNullable: true,
      }),
      appLocationText: new FormControl<string | null>(null, Validators.maxLength(100)),
      conceptInstallationNumber: new FormControl<string | null>(null, Validators.maxLength(8)),
    }),
    step3: new FormGroup<Step3Form>({
      selectedDefaultAccessGroups: new FormControl<DefaultAccessGroup[]>([], { nonNullable: true }),
      pricePolicyId: new FormControl<number | null>(null, Validators.required),
      facilities: new FormControl<LocationFacility[]>([], { nonNullable: true }),
      isLargeVehicleAccessible: new FormControl<boolean>(false, { nonNullable: true }),
    }),
    step4: new FormGroup<Step4Form>({
      product: new FormControl<LocationProduct | null>(null),
      agreementNumber: new FormControl<number | null>(null),
      payoutKickback: new FormControl<boolean>(false, { nonNullable: true }),
      kickbackId: new FormControl<number | null>(null),
      hasPowerPriceExclusion: new FormControl<boolean>(false, { nonNullable: true }),
    }),
    id: new FormControl<number | null>(null),
    chargingStationLocationIdentifier: new FormControl<string | null>(null),
  });

  locationDetails$: Observable<ChargingStationLocationDetails> = of(this.dialogData?.locationIdentifier).pipe(
    filter(id => this.service.isEditing()),
    switchMap(id =>
      this.chargingStationLocationService.getChargingStationLocationDetails(id!).pipe(
        take(1),
        shareReplay(1),
        tap(x => {
          this.step1.controls.organizationId.disable();
          this.step1.controls.departmentId.disable();
          this.step1.controls.organizationId.setValue(x.organizationId);
          this.step2.controls.name.disable();

          this.step3.controls.selectedDefaultAccessGroups.disable();
          this.initialHasOkPowerMeter = x.hasOkPowerMeter ?? false;
          this.lastHasOkPowerMeter = this.initialHasOkPowerMeter;
          this.initialPricePolicyId.set(x.defaultPricePolicy?.id ?? null);
          this.initialNetAreaAddress.set({ street: x.road, houseNumber: String(x.number), zipCode: x.postalCode });
          if (x.canHaveDynamicPricePolicy != null) {
            this.initialCanHaveDynamicPricePolicy.set(x.canHaveDynamicPricePolicy);
          } else {
            this.dynamicPricePolicyEligibility$
              .pipe(take(1))
              .subscribe(eligibility => this.initialCanHaveDynamicPricePolicy.set(eligibility.canHaveDynamicPricePolicy));
          }
          this.initialAddress.set({
            fullAddress: new Address(x.city, x.postalCode, x.road, x.number, x.letter, x.floor, x.door).fullAddress,
            darId: x.darId ? Guid.parse(x.darId) : undefined,
          });
          this.service.organizationChanged.next(x.organizationId);

          this.locationForm.patchValue({
            step1: { category: x.category },
            step2: {
              restricted: x.restricted,
              hasOkPowerMeter: x.hasOkPowerMeter ?? false,
              locationAppVisibilityStatus: x.appVisibilityStatus,
              locationAppVisibility: x.locationAppVisibility ?? LocationAppVisibility.Both,
              conceptInstallationNumber: x.conceptInstallationNumber?.toString() ?? null,
              latitude: String(x.latitude).replace('.', ','),
              longitude: String(x.longitude).replace('.', ','),
              appLocationText: x.appLocationText,
              addressWithoutDARValidation: x.darId == null,
              address: {
                street: x.road,
                houseNumber: String(x.number),
                houseLetter: x.letter ?? '',
                floor: x.floor ?? null,
                door: x.door ?? null,
                zipCode: x.postalCode,
                town: x.city,
                darId: x.darId ?? null,
              },
            },
            step3: { facilities: x.facilities ?? [], isLargeVehicleAccessible: x.isLargeVehicleAccessible ?? false },
            step4: {
              kickbackId: x.kickBack?.id ?? null,
              agreementNumber: x.kwhCompensationAgreementNumber ? Number(x.kwhCompensationAgreementNumber) : null,
              payoutKickback: x.kickBack != null,
              product: x.product ?? null,
              hasPowerPriceExclusion: x.hasPowerPriceExclusion ?? false,
            },
            id: x.chargingStationLocationRelationId,
            chargingStationLocationIdentifier: x.chargingStationLocationIdentifier,
          });

          const identifiers = this.step2.controls.meteringPointIdentifiers;
          identifiers.clear({ emitEvent: false });

          for (const identifier of x.meteringPointIdentifiers ?? []) {
            const suffix = identifier.startsWith('5713131') ? identifier.slice(7) : identifier;

            identifiers.push(
              new FormControl(suffix, {
                nonNullable: true,
                validators: [Validators.pattern(/^\d{11}$/)],
              }),
              { emitEvent: false }
            );
          }

          this.step3.controls.pricePolicyId.setValue(this.initialPricePolicyId());

          this.updatePricePolicyRequirement();
        })
      )
    ),
    shareReplay(1)
  );

  private readonly dynamicPricePolicyEligibility$ = dynamicPricePolicyEligibility(
    this.step2.controls.address.valueChanges,
    this.chargingStationLocationService
  ).pipe(shareReplay({ bufferSize: 1, refCount: false }));

  private readonly eligibility = toSignal(this.dynamicPricePolicyEligibility$, { initialValue: null });

  canHaveDynamicPricePolicy = computed(() => this.eligibility()?.canHaveDynamicPricePolicy ?? false);

  private readonly addressValue = toSignal(this.step2.controls.address.valueChanges, {
    initialValue: this.step2.controls.address.value,
  });

  private readonly netAreaRelevantAddressChanged = computed(() => {
    const initial = this.initialNetAreaAddress();
    return initial != null && netAreaAddressKey(initial) !== netAreaAddressKey(this.addressValue());
  });

  private readonly usesStoredEligibility = computed(
    () => this.initialCanHaveDynamicPricePolicy() != null && !this.netAreaRelevantAddressChanged()
  );

  private readonly validateNetAreaForPricePolicy = toSignal(
    inject(FeatureFlagService)
      .getFeatureFlag(FEATURE_FLAGS.VALIDATE_NET_AREA_FOR_PRICE_POLICY)
      .pipe(map(flag => flag.result)),
    { initialValue: false }
  );

  readonly netAreaCheckPending = computed(() => {
    const addressKey = netAreaAddressKey(this.addressValue());
    return (
      this.validateNetAreaForPricePolicy() &&
      !this.usesStoredEligibility() &&
      addressKey !== null &&
      addressKey !== this.eligibility()?.addressKey
    );
  });

  // The backend only re-resolves the net area when road, number or postal code change.
  readonly effectiveCanHaveDynamicPricePolicy = computed(() => {
    if (!this.validateNetAreaForPricePolicy()) {
      return true;
    }
    const initial = this.initialCanHaveDynamicPricePolicy();
    return initial != null && !this.netAreaRelevantAddressChanged() ? initial : this.canHaveDynamicPricePolicy();
  });

  protected readonly netAreaRemoved = computed(
    () =>
      this.validateNetAreaForPricePolicy() &&
      this.initialCanHaveDynamicPricePolicy() === true &&
      this.netAreaRelevantAddressChanged() &&
      this.hasCompleteAddress() &&
      !this.netAreaCheckPending() &&
      !this.canHaveDynamicPricePolicy()
  );

  private readonly hasCompleteAddress = computed(() => netAreaAddressKey(this.addressValue()) !== null);

  readonly showNoValidNetAreaWarning = computed(
    () => this.hasCompleteAddress() && !this.netAreaCheckPending() && !this.effectiveCanHaveDynamicPricePolicy()
  );

  isFormValid$: Observable<boolean>;

  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  get step1() {
    return this.locationForm.controls.step1;
  }

  get step2() {
    return this.locationForm.controls.step2;
  }

  get step3() {
    return this.locationForm.controls.step3;
  }

  get step4() {
    return this.locationForm.controls.step4;
  }

  constructor() {
    if (this.dialogData?.locationIdentifier && (this.dialogData?.location == null || this.dialogData?.location == undefined)) {
      throw new Error('Missing charging station location details for editing');
    }

    if (!this.service.isEditing() && this.dialogData?.organizationId != null) {
      this.step1.patchValue({
        organizationId: this.dialogData.organizationId,
        departmentId: this.dialogData.departmentId?.toString() ?? null,
      });
      this.step1.controls.organizationId.disable();
      this.step1.controls.departmentId.disable();
      this.service.organizationChanged.next(this.dialogData.organizationId);
    }

    this.isFormValid$ = this.locationForm.statusChanges.pipe(
      startWith(this.locationForm.status),
      map(() => this.locationForm.valid)
    );

    this.step2.controls.hasOkPowerMeter.valueChanges.pipe(startWith(this.step2.controls.hasOkPowerMeter.value)).subscribe(() => {
      if (this.step2.controls.hasOkPowerMeter.value) {
        this.step4.controls.hasPowerPriceExclusion.setValue(false, { emitEvent: false });
      }

      this.updateMeteringPointIdentifierValidationForOkPowerMeter();
      this.updatePricePolicyRequirement();
    });

    toObservable(computed(() => ({ netAreaRemoved: this.netAreaRemoved(), canHaveDynamic: this.effectiveCanHaveDynamicPricePolicy() })))
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updatePricePolicyRequirement());

    this.step2.controls.addressWithoutDARValidation.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      Object.values(this.step2.controls.address.controls).forEach(control => control.updateValueAndValidity());
    });

    this.loading$.next(false);
  }

  private updateMeteringPointIdentifierValidationForOkPowerMeter() {
    const identifiersControl = this.step2.controls.meteringPointIdentifiers;
    const hasOkPowerMeter = this.step2.controls.hasOkPowerMeter.value ?? false;

    if (hasOkPowerMeter) {
      identifiersControl.setValidators([Validators.required]);
    } else {
      identifiersControl.clearValidators();
    }

    identifiersControl.updateValueAndValidity({ emitEvent: false });
  }

  private updatePricePolicyRequirement() {
    const selectedDynamicNotAllowed = this.selectedPricePolicyIsDynamic && !this.effectiveCanHaveDynamicPricePolicy();

    if (!this.service.isEditing()) {
      if (selectedDynamicNotAllowed) {
        this.clearPricePolicy();
        this.step3.controls.pricePolicyId.markAsTouched();
      }
      return;
    }

    const hasOkPowerMeter = this.step2.controls.hasOkPowerMeter.value ?? false;
    const required = hasOkPowerMeter !== this.initialHasOkPowerMeter || this.netAreaRemoved();
    const wasRequired = this.pricePolicyChangeRequired();
    const okPowerMeterToggled = hasOkPowerMeter !== this.lastHasOkPowerMeter;
    const pricePolicyControl = this.step3.controls.pricePolicyId;
    this.lastHasOkPowerMeter = hasOkPowerMeter;

    if (required) {
      if (!wasRequired || okPowerMeterToggled || selectedDynamicNotAllowed) {
        this.clearPricePolicy();
      }
      pricePolicyControl.setValidators([Validators.required]);
      pricePolicyControl.markAsTouched();
      this.step3.markAsDirty();
    } else {
      pricePolicyControl.setValidators(this.initialPricePolicyId() != null ? [Validators.required] : []);
      if (wasRequired) {
        pricePolicyControl.setValue(this.initialPricePolicyId());
        this.selectedPricePolicyIsDynamic = false;
      }
    }

    pricePolicyControl.updateValueAndValidity();
    this.pricePolicyChangeRequired.set(required);
  }

  private clearPricePolicy() {
    this.step3.controls.pricePolicyId.setValue(null);
    this.selectedPricePolicyIsDynamic = false;
    this.pricePolicyResetTrigger.set({ reset: true });
  }

  protected onPricePolicySelected(pricePolicy: PricePolicy | null) {
    this.selectedPricePolicyIsDynamic = pricePolicy?.type === PricePolicyType.Dynamic;
  }

  private getLocationErrorMessage(httpError: HttpErrorResponse, fallback: string): string {
    const code = (httpError.error as ErrorMessage | null)?.code;
    return (code != null ? LOCATION_ERROR_MESSAGES[code] : undefined) ?? fallback;
  }

  public handleSave() {
    if (this.service.saving()) {
      return;
    }

    if (this.service.isEditing()) {
      this.editAndClose();
    } else {
      this.createAndClose();
    }
  }

  public editAndClose = () => {
    const identifier = this.locationForm.controls.chargingStationLocationIdentifier.value;
    if (identifier == null) {
      return;
    }

    this.service.saving.set(true);
    const request = this.service.mapUpdateRequest(this.locationForm, this.pricePolicyChangeRequired());

    this.chargingStationLocationService
      .update(identifier, request)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.snackbarService.success('Lokationen blev succesfuldt redigeret.');
          this.service.saving.set(false);
          this.dialogRef.close(identifier);
        },
        error: (httpError: HttpErrorResponse) => {
          this.service.saving.set(false);
          this.snackbarService.error(this.getLocationErrorMessage(httpError, 'Der skete en ukendt fejl under redigering'));
        },
      });
  };

  public createAndClose = () => {
    this.service.saving.set(true);

    const request = this.service.mapCreateRequest(this.locationForm);

    const createObservable = this.chargingStationLocationService.create({
      ...request,
      product: this.step4.controls.product.value ?? undefined,
    } as CreateChargingStationLocation);

    createObservable.pipe(take(1)).subscribe({
      next: async resp => {
        const pageEvent = new PageEvent();
        pageEvent.pageSize = 1;
        const placeholderSearchEvent = new ChargingStationLocationSearchEvent(pageEvent);
        placeholderSearchEvent.chargingStationLocationIdentifier = resp.toString();
        let attempts = 0;
        const maxAttempts = 5;

        const checkLocation = () => {
          this.chargingStationLocationService
            .getChargingStationLocations(placeholderSearchEvent)
            .pipe(
              map(result => result.items[0] ?? null),
              catchError(() => of(null))
            )
            .subscribe(location => {
              if (location || attempts >= maxAttempts) {
                this.snackbarService.success(
                  'Lokationen blev succesfuldt oprettet.' +
                    (this.router.url.includes('/organizations') ? '' : ` "${request.name}" bør dukke op i listen når dette vindue lukkes`)
                );
                //waiting for Backend to handle the cslcreated event before closing the dialog
                setTimeout(() => {
                  this.dialogRef.close(resp);
                }, 2500);
              } else {
                attempts++;
                setTimeout(checkLocation, 1000);
              }
            });
        };

        checkLocation();
      },
      error: (httpError: HttpErrorResponse) => {
        this.service.saving.set(false);
        this.snackbarService.error(this.getLocationErrorMessage(httpError, 'Der skete en ukendt fejl under oprettelse af lokationen.'));
      },
    });
  };
}
